import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';
import { formatDate } from '../../utils/formatters.js';
import { countBy, percent } from '../../utils/insightStats.js';

import { ApplicationCreateModal } from './ApplicationCreateModal.jsx';

const STAGES = ['APPLIED', 'SCREEN', 'INTERVIEW', 'OFFER', 'HIRED', 'REJECTED'];

function stageColor(palette, stage) {
  const s = palette?.series || [];
  const map = {
    APPLIED: s[4],
    SCREEN: s[2],
    INTERVIEW: s[0],
    OFFER: s[3],
    HIRED: s[1],
    REJECTED: palette?.expense,
  };
  return map[stage] ?? palette?.axis;
}

export function ApplicationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [stageFilter, setStageFilter] = useState('');

  const applications = useQuery({
    queryKey: ['hr', 'applications', stageFilter],
    queryFn: () => hrApi.listApplications({ stage: stageFilter || undefined }).then((r) => r.items || []),
  });
  const postings = useQuery({
    queryKey: ['hr', 'job-postings'],
    queryFn: () => hrApi.listJobPostings().then((r) => r.items || []),
  });
  const candidates = useQuery({
    queryKey: ['hr', 'candidates'],
    queryFn: () => hrApi.listCandidates().then((r) => r.items || []),
  });

  const postingById = useMemo(
    () => new Map((postings.data || []).map((p) => [p.id, p])),
    [postings.data],
  );
  const candidateById = useMemo(
    () => new Map((candidates.data || []).map((c) => [c.id, c])),
    [candidates.data],
  );

  const palette = useChartPalette();
  const stats = useMemo(() => {
    const rows = applications.data || [];
    const counts = countBy(rows, (r) => r.stage);
    const stages = STAGES.filter((s) => counts[s]);
    const hired = counts.HIRED || 0;
    return {
      total: rows.length,
      stages,
      counts,
      active: rows.filter((r) => r.stage !== 'HIRED' && r.stage !== 'REJECTED').length,
      hired,
      hireRate: percent(hired, rows.length),
    };
  }, [applications.data]);

  const setStage = useMutation({
    mutationFn: ({ id, stage }) => hrApi.updateApplication(id, { stage }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hr', 'applications'] }),
  });

  const columns = [
    {
      key: 'candidate',
      label: t('rec.applications.col.candidate'),
      render: (r) => {
        const c = candidateById.get(r.candidateId);
        return c ? `${c.firstName} ${c.lastName}` : r.candidateId.slice(0, 8);
      },
    },
    {
      key: 'posting',
      label: t('rec.applications.col.posting'),
      render: (r) => postingById.get(r.jobPostingId)?.title || r.jobPostingId.slice(0, 8),
    },
    { key: 'createdAt', label: t('col.date'), render: (r) => formatDate(r.createdAt) },
    {
      key: 'stage',
      label: t('rec.applications.col.stage'),
      render: (r) => (
        <select
          className="select"
          style={{ minWidth: 140 }}
          value={r.stage}
          onChange={(e) => setStage.mutate({ id: r.id, stage: e.target.value })}
          disabled={setStage.isPending}
        >
          {STAGES.map((s) => (
            <option key={s} value={s}>{t(`stage.${s}`)}</option>
          ))}
        </select>
      ),
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('rec.applications.title')}</h1>
          <p className="muted">{t('rec.applications.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('rec.applications.action.new')}
          </Button>
        </div>
      </header>
      {!stageFilter && stats.total > 0 && (
        <InsightBar
          title={t('insight.byStage')}
          labels={stats.stages.map((s) => t(`stage.${s}`))}
          values={stats.stages.map((s) => stats.counts[s])}
          colors={stats.stages.map((s) => stageColor(palette, s))}
          metrics={[
            { label: t('insight.count'), value: stats.total },
            { label: t('insight.active'), value: stats.active },
            { label: t('stage.HIRED'), value: stats.hired },
            { label: t('rec.applications.insight.hireRate'), value: `${stats.hireRate} %` },
          ]}
        />
      )}
      <div className="toolbar">
        <select className="select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
          <option value="">{t('rec.applications.filterStage.all')}</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>{t(`stage.${s}`)}</option>
          ))}
        </select>
      </div>
      {applications.error && <div className="alert danger">{applications.error.message}</div>}
      <Table
        columns={columns}
        rows={applications.data}
        isLoading={applications.isLoading}
        emptyMessage={t('rec.applications.empty')}
      />
      <ApplicationCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </section>
  );
}
